import type { GoalAccent, ObjectiveStatus } from "./api";

// ─── Objective status presentation ─────────────────────────────────────────
// Mirrors the canonical mapping in features/objectives/constants.ts so a linked
// objective's status reads identically here and on the Objectives page. (Kept
// as a small local copy rather than a cross-feature import to avoid coupling.)

export type StatusVariant = "success" | "warning" | "danger" | "info" | "neutral";

export const OBJECTIVE_STATUS_LABEL: Record<ObjectiveStatus, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  at_risk: "At risk",
  on_hold: "On hold",
  complete: "Complete",
};

export const OBJECTIVE_STATUS_VARIANT: Record<ObjectiveStatus, StatusVariant> = {
  not_started: "neutral",
  in_progress: "info",
  at_risk: "danger",
  on_hold: "warning",
  complete: "success",
};

// ─── Goal accent → utility classes ──────────────────────────────────────────
// Literal strings so Tailwind's JIT emits them (never interpolate). Used by
// GoalCard; the GoalFlowStory DS component carries its own equivalent maps.

export const ACCENT_BAR: Record<GoalAccent, string> = {
  brand: "bg-brand",
  power: "bg-power",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  neutral: "bg-neutral",
  purple: "bg-purple",
  teal: "bg-teal",
  orange: "bg-orange",
};

export const ACCENT_TEXT: Record<GoalAccent, string> = {
  brand: "text-brand",
  power: "text-power",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
  neutral: "text-foreground",
  purple: "text-purple",
  teal: "text-teal",
  orange: "text-orange",
};

export const ACCENT_OPTIONS: { value: GoalAccent; label: string }[] = [
  { value: "power", label: "Power blue" },
  { value: "brand", label: "Brand cyan" },
  { value: "success", label: "Green" },
  { value: "warning", label: "Amber" },
  { value: "danger", label: "Red" },
  { value: "info", label: "Info" },
  { value: "purple", label: "Purple" },
  { value: "teal", label: "Teal" },
  { value: "orange", label: "Orange" },
  { value: "neutral", label: "Neutral" },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Slug for a goal `key` derived from its title (e.g. "Top of Funnel" →
 *  "top_of_funnel"). Keys are unique per year; the backend 409s on collision. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

export function formatPct(n: number | null | undefined): string {
  if (n == null) return "—";
  return `${Math.round(n)}%`;
}
